// frontend/src/pages/crm/FieldSessionsPage.tsx
// Field Sessions Phase 2 (2026-07-29) — the rep's day/range view (spec §7).
//
// 🔒 ONE date control. A one-day range IS a single day (Manak, 2026-07-29):
// there is no day picker beside a range picker. Two <input type="date"> fields
// feed the SAME `GET /field-sessions?from=&to=` call, and `data.range.isSingleDay`
// only decides the heading wording — never which widget renders.
//
// 🔒 The conversion matrix is RENDERED from `data.matrix`, never recomputed
// here. A client-side recount would drift from the server's the moment either
// changes, and then two screens would disagree about the same day.
//
// 🔒 NOT a FieldDayPlan view. A plan is the manager's INTENT for a route; a
// session is what the rep actually did.
//
// Spec: docs/superpowers/specs/2026-07-29-field-activity-and-sessions-design.md

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/utils/api';
// 🔒 Phase 5: these were local copies here AND in FieldAgentsPage. Extracted to
// fieldFormat.tsx so the rep's page, the manager board and the drill-down can
// never disagree about the ladder, the money or the "logged vs scheduled" rule.
import {
  Badge, DOT, Empty, Section, Tile,
  hhmm, isLoggedMeeting, meetingSubject, ymd,
} from './fieldFormat';

/**
 * A FIELD DAY, as the server's `buildFieldDays` folds it.
 *
 * 🔒 THE DAY IS THE UNIT (Manak, 2026-07-29): "it should be a days session".
 * Two runs before lunch and one at four are ONE day with ONE total. The
 * FieldSession rows still exist — `data.sessions` is untouched — this is the
 * projection over them.
 */
type FieldRun = {
  sessionId: string;
  startedAt: string;
  endedAt: string | null;
  /** Start == end. Never render a span for one of these. */
  zeroLength: boolean;
};

type FieldDay = {
  date: string;
  sessionIds: string[];
  userIds: string[];
  runs: FieldRun[];
  totalDistanceMetres: number;
  claimState: string | null;
  leadCount: number;
  meetingCount: number;
};

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * `2026-07-27` → `Mon 27 Jul`.
 *
 * 🔒 Formatted from the server's DATE KEY, never from an instant: parsing the
 * day's first run and formatting it in the browser's zone would put a late
 * evening run on the wrong calendar day for anyone not on IST. Mirrors
 * `dayHeading` in the mobile `field_day.dart`.
 */
function dayHeading(date: string): string {
  const parts = date.split('-');
  if (parts.length !== 3) return date;
  const [y, m, d] = parts.map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d) || m < 1 || m > 12) return date;
  const at = new Date(Date.UTC(y, m - 1, d, 12));
  if (at.getUTCMonth() !== m - 1 || at.getUTCDate() !== d) return date;
  // getUTCDay() is 0=Sun; the table is Mon-first.
  return `${WEEKDAYS[(at.getUTCDay() + 6) % 7]} ${d} ${MONTHS[m - 1]}`;
}

/**
 * 🔒 NOT `km()` from fieldFormat. That helper's contract is null == unknown,
 * zero == a real measured zero, which is right for one session. A DAY total is
 * a SUM, so zero means nothing on the day was ever measured — rendering
 * "0.0 km" there would claim a measurement that was never taken.
 */
function dayDistance(metres: number): string {
  return metres > 0 ? `${(metres / 1000).toFixed(1)} km` : 'not measured';
}

function numberWord(n: number): string {
  return n === 1 ? 'One' : n === 2 ? 'Two' : n === 3 ? 'Three' : String(n);
}

/**
 * The quiet sub-line. 🔒 A zero-length run contributes NO span — that is the
 * `11:13 → 11:13` complaint. An open run reads `since HH:mm`, which is a
 * different fact from a run that never had a duration.
 */
function runsLabel(d: FieldDay): string {
  const runs = d.runs ?? [];
  if (runs.length === 0) return 'No run recorded';
  const spans = runs
    .filter((r) => !r.zeroLength)
    .map((r) => (r.endedAt ? `${hhmm(r.startedAt)}–${hhmm(r.endedAt)}` : `since ${hhmm(r.startedAt)}`));
  const count = `${numberWord(runs.length)} ${runs.length === 1 ? 'run' : 'runs'}`;
  return spans.length === 0 ? count : `${count} · ${spans.join(', ')}`;
}

function sitesLabel(d: FieldDay): string {
  const parts = [
    `${d.leadCount} ${d.leadCount === 1 ? 'site' : 'sites'}`,
    `${d.meetingCount} ${d.meetingCount === 1 ? 'meeting' : 'meetings'}`,
  ];
  // The fold is by DATE alone, so a team-scoped read can blend two reps into
  // one card. Say so rather than let it read as one person's day.
  if ((d.userIds ?? []).length > 1) parts.push(`${d.userIds.length} people`);
  return parts.join(' · ');
}

/** Never blank: "no claim cycle yet" is Unclaimed, which is what a rep needs. */
function claimStateLabel(claimState: string | null): string {
  switch (claimState) {
    case null:
    case 'draft': return 'Unclaimed';
    case 'submitted': return 'Submitted';
    case 'approved': return 'Approved';
    case 'rejected': return 'Rejected';
    // Shown verbatim: swallowing an unknown state would hide a server change
    // behind a screen that looks correct.
    default: return claimState;
  }
}

type FieldLead = {
  id: string;
  fieldActivityStatus: string | null;
  fieldLeadTitle: string | null;
  leadLat: number | null;
  leadLng: number | null;
  createdAt: string;
  captureId: string | null;
};

type ConversionMatrix = {
  red: number; yellow: number; orange: number; green: number; promoted: number;
  captured: number; withAWayIn: number; promotedPct: number; wayInPct: number;
};

const EMPTY_MATRIX: ConversionMatrix = {
  red: 0, yellow: 0, orange: 0, green: 0, promoted: 0,
  captured: 0, withAWayIn: 0, promotedPct: 0, wayInPct: 0,
};

// Same ladder as FieldActivityPage — keep the two in sync.
export function FieldSessionsPage() {
  const today = ymd(new Date());
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState<FieldDay[]>([]);
  /**
   * 🔒 Whose data is on screen, as the SERVER resolved it. Never re-derived
   * here: the server clamps a stale pick, and a client that guesses would
   * disagree with the answer it was handed.
   */
  const [ownerScope, setOwnerScope] = useState<string | null>(null);
  const [leads, setLeads] = useState<FieldLead[]>([]);
  const [meetings, setMeetings] = useState<any[]>([]);
  const [matrix, setMatrix] = useState<ConversionMatrix>(EMPTY_MATRIX);
  const [isSingleDay, setIsSingleDay] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/field-sessions', { params: { from, to } });
      const data = res.data?.data ?? {};
      setDays(Array.isArray(data.days) ? data.days : []);
      setOwnerScope(typeof data.owner?.scope === 'string' ? data.owner.scope : null);
      setLeads(Array.isArray(data.leads) ? data.leads : []);
      setMeetings(Array.isArray(data.meetings) ? data.meetings : []);
      setMatrix({ ...EMPTY_MATRIX, ...(data.matrix ?? {}) });
      setIsSingleDay(data.range?.isSingleDay !== false);
    } catch (e: any) {
      setError(e?.message || 'Could not load field sessions');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const heading = isSingleDay ? from : `${from} → ${to}`;
  /** 🔒 Fails CLOSED. Anything that is not 'mine' — including a missing scope. */
  const ownsData = ownerScope === 'mine';

  return (
    <div style={{ padding: 24, maxWidth: 980, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Field Sessions</h1>
        <Link to="/field-activity" style={{ fontSize: 14, color: '#6B7FD6' }}>Field Activity board →</Link>
      </div>
      {/* 🔒 The owner gate (Manak, 2026-07-29): "whats it doing being visible to
          mamta in first place". `data.owner.scope` is the SERVER's answer, and
          `ownsData` fails CLOSED — anything that is not 'mine' is somebody
          else's board. Today this page sends no `ownerFilter`, so the scope is
          always 'mine'; the gate exists so the first owner-only control added
          here (Squad C's day claim) is born gated rather than retro-fitted.

          It already earns its keep on the copy: "what YOU did … your own
          location history" is a lie on a rep's board seen by their manager. */}
      <p style={{ color: '#6b7280', marginTop: 0, fontSize: 13 }}>
        {ownsData ? (
          <>
            What you actually did on the ground — when you started and stopped, how far you
            travelled, and the sites you captured. Distance is measured from your own location
            history, never typed from scratch.
          </>
        ) : (
          <>
            Somebody else&rsquo;s days on the ground — when they started and stopped, how far they
            travelled, and the sites they captured. Read only: travel claims belong to the person
            who walked them.
          </>
        )}
      </p>

      {/* ONE date control. A one-day range IS a single day, so both inputs feed
          the same request; leaving them equal is the day view. */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '16px 0' }}>
        <input
          type="date"
          value={from}
          max={to}
          onChange={(e) => setFrom(e.target.value)}
          style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6 }}
        />
        <span style={{ color: '#9ca3af' }}>→</span>
        <input
          type="date"
          value={to}
          min={from}
          onChange={(e) => setTo(e.target.value)}
          style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6 }}
        />
        <button
          onClick={() => { setFrom(today); setTo(today); }}
          style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer' }}
        >
          Today
        </button>
        <span style={{ marginLeft: 'auto', fontSize: 13, color: '#6b7280' }}>{heading}</span>
      </div>

      {error && (
        <div style={{ padding: 12, background: '#fef2f2', color: '#b91c1c', borderRadius: 8, marginBottom: 16 }}>
          {error}
        </div>
      )}
      {loading && <p style={{ color: '#6b7280' }}>Loading…</p>}

      {!loading && (
        <>
          {/* Conversion matrix — the server's numbers, rendered verbatim. */}
          <h2 style={{ fontSize: 12, letterSpacing: 0.6, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase' }}>
            Conversion
          </h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
            <Tile label="Captured" value={matrix.captured} color="#6b7280" />
            <Tile label="No number" value={matrix.red} color={DOT.red} />
            <Tile label="Site contact" value={matrix.yellow} color={DOT.yellow} />
            <Tile label="Referred on" value={matrix.orange} color={DOT.orange} />
            <Tile label="DM found" value={matrix.green} color={DOT.green} />
            <Tile label="Promoted" value={matrix.promoted} color={DOT.promoted} />
          </div>
          <p style={{ fontSize: 13, color: '#6b7280', marginTop: 0 }}>
            {/* `withAWayIn` is the number that matters: orange counts, because a route
                to the decision maker is real progress even before a promotion. */}
            {matrix.withAWayIn} with a way in ({matrix.wayInPct}%) · {matrix.promotedPct}% promoted
          </p>

          {/* 🔒 FIELD DAYS, not sessions (Manak, 2026-07-29). A table of bare
              `11:13` times across a multi-day range read as unsorted noise even
              though it was sorted; every card now carries its DATE, and two
              runs on one day are ONE card with ONE total.

              🔒 NO CLAIM CONTROL here. Squad C re-models the claim onto a day
              aggregate; a day card wired to the existing per-session endpoint
              would let the same kilometres be claimed twice — once as a day,
              once inside a period. This renders claim STATE only. */}
          <Section title="Field days">
            {days.length === 0 ? (
              <Empty>No field day recorded for this period.</Empty>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {days.map((d) => (
                  <li
                    key={d.date}
                    style={{
                      border: '1px solid #f3f4f6', borderRadius: 10,
                      padding: '12px 14px', marginBottom: 8,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                      <span style={{ fontWeight: 800, fontSize: 15, color: '#111827' }}>
                        {dayHeading(d.date)}
                      </span>
                      <span
                        style={{
                          marginLeft: 'auto', fontWeight: 700, fontSize: 14,
                          color: d.totalDistanceMetres > 0 ? '#111827' : '#9ca3af',
                        }}
                      >
                        {dayDistance(d.totalDistanceMetres)}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: '#374151', marginTop: 4 }}>
                      {sitesLabel(d)}
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                      {runsLabel(d)}
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <Badge>{claimStateLabel(d.claimState)}</Badge>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="Field leads">
            {leads.length === 0 ? (
              <Empty>No sites captured in this period.</Empty>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {leads.map((l) => (
                  <li key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderTop: '1px solid #f3f4f6' }}>
                    <span
                      style={{
                        width: 12, height: 12, borderRadius: '50%', flexShrink: 0,
                        background: DOT[l.fieldActivityStatus ?? ''] ?? '#9ca3af',
                      }}
                    />
                    <Link to={`/leads/${l.id}`} style={{ fontWeight: 600, color: '#111827', textDecoration: 'none' }}>
                      {l.fieldLeadTitle?.trim() || 'Unnamed site'}
                    </Link>
                    <span style={{ color: '#6b7280', fontSize: 13 }}>{l.fieldActivityStatus ?? 'unknown'}</span>
                    {l.leadLat != null && l.leadLng != null && (
                      <span style={{ marginLeft: 'auto', color: '#9ca3af', fontSize: 12 }}>
                        {l.leadLat.toFixed(4)}, {l.leadLng.toFixed(4)}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Section>

          {/* Phase 3 (2026-07-29) — populated from ScheduledMeeting, which now
              carries `leadImportId` so a meeting held at a raw field site
              (no Contact, no Deal) finally has somewhere to live.

              🔒 "logged" vs "scheduled" is the entire reason `source` exists:
              a manager must be able to tell a walk-in the rep recorded after
              the fact from a planned calendar meeting. Do not collapse them.

              The empty state stays for genuinely empty days. */}
          <Section title="Meetings">
            {meetings.length === 0 ? (
              <Empty>Meetings logged in the field will appear here.</Empty>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {meetings.map((m: any, i: number) => {
                  const logged = isLoggedMeeting(m);
                  return (
                    <li
                      key={m?.id ?? i}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 0', borderTop: '1px solid #f3f4f6',
                      }}
                    >
                      <span style={{ color: '#6b7280', fontSize: 13, minWidth: 52 }}>
                        {hhmm(m?.scheduledAt)}
                      </span>
                      <span style={{ fontWeight: 600, color: '#111827' }}>
                        {meetingSubject(m)}
                      </span>
                      <span style={{ color: '#6b7280', fontSize: 13 }}>
                        {[
                          typeof m?.durationMin === 'number' ? `${m.durationMin} min` : null,
                          m?.mode,
                          (m?.addressLine1 ?? '').trim() || null,
                        ].filter(Boolean).join(' · ')}
                      </span>
                      <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                        <Badge>{logged ? 'logged' : 'scheduled'}</Badge>
                        {m?.status && m.status !== 'scheduled' && <Badge>{m.status}</Badge>}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </Section>
        </>
      )}
    </div>
  );
}
